import { z } from 'zod';
import type { RejectCode } from './errors.ts';
import { CharacterSlotId, ConnectionStatus, ParticipantRole } from './participant.ts';
import { VoiceScope } from './voice.ts';
import { LIMITS } from './constants.ts';

/** cooperative: everyone together; individual: each for themselves; teams: fixed team assignments. Data and permissions only. */
export const Participation = z.enum(['cooperative', 'individual', 'teams']);
export type Participation = z.infer<typeof Participation>;

export const TeamAssignment = z.object({
  teamId: z.string(),
  label: z.string(),
  participantIds: z.array(z.string()),
});
export type TeamAssignment = z.infer<typeof TeamAssignment>;

/**
 * Module manifest. Validated at registration and before every launch. The session framework uses it
 * to check participant counts and team arrangement; it never interprets game rules.
 */
export const ModuleManifest = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  version: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  /** Disposable POC modules are marked so nobody mistakes them for product decisions. */
  disposable: z.boolean(),
  players: z.object({
    min: z.number().int().min(1).max(LIMITS.MAX_PLAYERS),
    max: z.number().int().min(1).max(LIMITS.MAX_PLAYERS),
  }),
  participation: Participation,
  /** Required when participation is `teams`. */
  teams: z
    .object({
      count: z.number().int().min(2).max(LIMITS.MAX_PLAYERS),
      minPerTeam: z.number().int().min(1),
      maxPerTeam: z.number().int().min(1),
    })
    .nullable(),
  voice: z.object({ scope: VoiceScope }),
  capabilities: z.object({
    /** Only a root instance with this flag may emit `launch_child`, and only for IDs in `childModuleIds`. */
    launchesChildren: z.boolean(),
    childModuleIds: z.array(z.string()),
    usesRandom: z.boolean(),
    hasPrivateData: z.boolean(),
    /** Client renderer key resolved by the client module registry. */
    renderer: z.string(),
  }),
  launchableFromLobby: z.boolean(),
});
export type ModuleManifest = z.infer<typeof ModuleManifest>;

export const InstanceStatus = z.enum(['active', 'completed', 'abandoned']);
export type InstanceStatus = z.infer<typeof InstanceStatus>;

/** Deadline as seen by clients: running (absolute server epoch ms) or paused (remaining ms). */
export const DeadlinePublic = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('running'), id: z.string(), at: z.number(), label: z.string().nullable() }),
  z.object({ mode: z.literal('paused'), id: z.string(), remainingMs: z.number(), label: z.string().nullable() }),
]);
export type DeadlinePublic = z.infer<typeof DeadlinePublic>;

/**
 * Result envelope returned exactly once to the parent (or to the session when the module is the root).
 * No assumption of score, winner or reward. `payload` is PUBLIC: it is shown to every client including spectators
 * in session results. Anything private (e.g. individual picks) belongs in `privatePayload`, which the engine delivers
 * only to the parent module's `onChildResult` and strips from every client projection.
 */
export const ModuleResult = z.object({
  instanceId: z.string(),
  moduleId: z.string(),
  moduleVersion: z.string(),
  resultSchemaVersion: z.number().int().min(1),
  status: z.enum(['completed', 'abandoned']),
  participantIds: z.array(z.string()),
  teams: z.array(TeamAssignment).nullable(),
  startedAt: z.number(),
  endedAt: z.number(),
  summary: z.string().nullable(),
  payload: z.unknown(),
  privatePayload: z.unknown().nullable(),
});
export type ModuleResult = z.infer<typeof ModuleResult>;

/** The client-facing shape of a result: identical to ModuleResult with `privatePayload` removed. */
export const ModuleResultPublic = ModuleResult.omit({ privatePayload: true });
export type ModuleResultPublic = z.infer<typeof ModuleResultPublic>;

export function toPublicResult(result: ModuleResult): ModuleResultPublic {
  const { privatePayload: _private, ...rest } = result;
  return rest;
}

export const EventScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('public') }),
  z.object({ kind: z.literal('participants'), participantIds: z.array(z.string()) }),
  z.object({ kind: z.literal('team'), teamId: z.string() }),
]);
export type EventScope = z.infer<typeof EventScope>;

/** Ephemeral module event (dice roll, movement animation, reveal). Not part of canonical state; not replayed on reconnect. */
export const ModuleEvent = z.object({
  name: z.string().min(1),
  payload: z.unknown(),
  scope: EventScope,
});
export type ModuleEvent = z.infer<typeof ModuleEvent>;

/**
 * Effects a module may request from the engine.
 * Semantics (engine-enforced, see ARCHITECTURE §4):
 * - All effects of a transition are validated before any is applied; an invalid list rejects the whole transition
 *   with `internal` and leaves state unchanged (this is a module bug, surfaced loudly).
 * - At most one `complete`, and it must be last. `launch_child` and `complete` cannot share a list.
 * - `launch_child` is allowed only from the root instance, only when the manifest declares `launchesChildren` and the
 *   module ID is in `childModuleIds`, and only while no child is active. Modules should guard with `ctx.childInstanceId`.
 * - `set_deadline` re-arms an existing ID (replacing it). `ms` is bounded by LIMITS.MAX_DEADLINE_MS.
 */
export const Effect = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('set_deadline'),
    id: z.string().min(1),
    ms: z.number().int().positive().max(LIMITS.MAX_DEADLINE_MS),
    label: z.string().optional(),
    /** Private deadlines remain persisted and scheduled, but are omitted from every client projection. */
    visibility: z.enum(['public', 'private']).optional(),
  }),
  z.object({ type: z.literal('clear_deadline'), id: z.string().min(1) }),
  z.object({ type: z.literal('emit'), event: ModuleEvent }),
  z.object({
    type: z.literal('launch_child'),
    moduleId: z.string(),
    participantIds: z.array(z.string()).min(1),
    teams: z.array(TeamAssignment).nullable().optional(),
    config: z.unknown().optional(),
    seed: z.number().int().optional(),
  }),
  z.object({
    type: z.literal('complete'),
    status: z.enum(['completed', 'abandoned']),
    payload: z.unknown(),
    privatePayload: z.unknown().optional(),
    summary: z.string().optional(),
  }),
  z.object({ type: z.literal('request_pause'), detail: z.string().min(1).max(200) }),
]);
export type Effect = z.infer<typeof Effect>;

/** Validates effect-list semantics independent of session state. Returns an error message or null. */
export function validateEffectList(effects: Effect[]): string | null {
  const completes = effects.filter((e) => e.type === 'complete').length;
  if (completes > 1) return 'more than one complete effect';
  if (completes === 1 && effects[effects.length - 1]?.type !== 'complete') return 'complete must be the last effect';
  if (completes === 1 && effects.some((e) => e.type === 'launch_child')) return 'launch_child and complete cannot share a transition';
  if (effects.filter((e) => e.type === 'launch_child').length > 1) return 'more than one launch_child effect';
  return null;
}

export interface ModuleParticipant {
  id: string;
  displayName: string;
  role: ParticipantRole;
  isHost: boolean;
  characterSlot: CharacterSlotId | null;
  teamId: string | null;
  connection: ConnectionStatus;
  simulated: boolean;
}

export interface ModuleContext {
  sessionId: string;
  instanceId: string;
  moduleId: string;
  /** Server time in epoch ms at the start of the transition. */
  now: number;
  /** Participants assigned to this instance (not the whole session). */
  participants: ModuleParticipant[];
  teams: TeamAssignment[] | null;
  parentInstanceId: string | null;
  /** Active child of this instance, if any. Guard `launch_child` with this. */
  childInstanceId: string | null;
  /** Engine-owned deterministic random source; each call is persisted with the transition. */
  random: () => number;
  randomInt: (minInclusive: number, maxInclusive: number) => number;
}

export interface ModuleActor {
  participantId: string;
  role: ParticipantRole;
  isHost: boolean;
  teamId: string | null;
  simulated: boolean;
}

export interface ActionContext extends ModuleContext {
  /** Identity established by the connection, never by the payload. */
  actor: ModuleActor;
  actionId: string;
}

export type ParticipantEvent =
  | { type: 'disconnected'; participantId: string }
  | { type: 'reconnected'; participantId: string }
  | { type: 'removed'; participantId: string }
  | { type: 'left'; participantId: string };

export type Viewer =
  | { kind: 'participant'; participantId: string; role: ParticipantRole; isHost: boolean; teamId: string | null }
  | { kind: 'spectator' };

export interface ProjectionContext {
  sessionId: string;
  instanceId: string;
  moduleId: string;
  now: number;
  participants: ModuleParticipant[];
  teams: TeamAssignment[] | null;
  deadlines: DeadlinePublic[];
  paused: boolean;
  parentInstanceId: string | null;
  childInstanceId: string | null;
}

export interface RecoveryInfo {
  /** Wall-clock gap between the last persisted transition and the restart. */
  downtimeMs: number;
  /** Whether the session was already paused before the restart. */
  wasPaused: boolean;
}

export interface TransitionResult<S> {
  state: S;
  effects?: Effect[];
}

export type TransitionOutcome<S> =
  | ({ ok: true } & TransitionResult<S>)
  | { ok: false; code: RejectCode; message?: string };

/**
 * Server-side module definition. Pure functions over serialisable state; no timers, sockets or renderer objects.
 * The engine calls these inside a per-session transaction and validates every effect it returns.
 * Legal effects per hook: `handleAction`, `onDeadline`, `onChildResult`, `onParticipantEvent` may return any effect;
 * `init`, `onPause`, `onResume`, `recover` may return deadline and emit effects only (no complete/launch_child).
 */
export interface ModuleDefinition<S = unknown, C = unknown, A = unknown> {
  manifest: ModuleManifest;
  configSchema: z.ZodType<C, unknown>;
  defaultConfig: C;
  actionSchema: z.ZodType<A, unknown>;
  /** Persisted state is validated with this schema on recovery; a mismatch quarantines the session (paused, actions rejected). */
  stateSchema: z.ZodType<S, unknown>;
  stateVersion: number;
  init(ctx: ModuleContext, config: C): TransitionResult<S>;
  handleAction(ctx: ActionContext, state: S, action: A): TransitionOutcome<S>;
  /** Fires once per armed deadline; re-arming the same ID later is allowed. */
  onDeadline?(ctx: ModuleContext, state: S, deadlineId: string): TransitionResult<S>;
  /** Parent modules receive each child result exactly once, including `privatePayload`. */
  onChildResult?(ctx: ModuleContext, state: S, result: ModuleResult): TransitionResult<S>;
  onParticipantEvent?(ctx: ModuleContext, state: S, event: ParticipantEvent): TransitionResult<S>;
  onPause?(ctx: ModuleContext, state: S, reason: string): TransitionResult<S>;
  onResume?(ctx: ModuleContext, state: S): TransitionResult<S>;
  /** Called after a restart, before the host resumes, so the module can reconcile a state it was mid-way through. */
  recover?(ctx: ModuleContext, state: S, info: RecoveryInfo): TransitionResult<S>;
  /** Called once when the instance completes, is abandoned, or the session ends. Return value ignored. */
  dispose?(ctx: ModuleContext, state: S): void;
  /** Participants whose disconnect should pause the instance. Default: all assigned players. */
  essentialParticipants?(ctx: ModuleContext, state: S): string[];
  /** Server-side projection. Must omit other participants' private data for participant and spectator viewers. */
  project(ctx: ProjectionContext, state: S, viewer: Viewer): unknown;
}

export function defineModule<S, C, A>(definition: ModuleDefinition<S, C, A>): ModuleDefinition<S, C, A> {
  ModuleManifest.parse(definition.manifest);
  return definition;
}

/** Validates a launch against a manifest. Returns null when valid or a rejection code. */
export function validateLaunch(
  manifest: ModuleManifest,
  participantIds: string[],
  teams: TeamAssignment[] | null,
): { code: RejectCode; message: string } | null {
  const n = participantIds.length;
  if (new Set(participantIds).size !== n) {
    return { code: 'invalid_payload', message: 'Duplicate participant IDs in launch' };
  }
  if (n < manifest.players.min || n > manifest.players.max) {
    return {
      code: 'unsupported_participant_count',
      message: `${manifest.name} supports ${manifest.players.min}-${manifest.players.max} players, got ${n}`,
    };
  }
  if (manifest.participation === 'teams') {
    if (!manifest.teams) return { code: 'invalid_team_config', message: 'Module declares teams without a team spec' };
    if (!teams || teams.length !== manifest.teams.count) {
      return { code: 'invalid_team_config', message: `${manifest.name} requires ${manifest.teams.count} teams` };
    }
    const seen = new Set<string>();
    for (const team of teams) {
      if (team.participantIds.length < manifest.teams.minPerTeam || team.participantIds.length > manifest.teams.maxPerTeam) {
        return { code: 'invalid_team_config', message: `Team ${team.label} has ${team.participantIds.length} members` };
      }
      for (const id of team.participantIds) {
        if (seen.has(id) || !participantIds.includes(id)) {
          return { code: 'invalid_team_config', message: `Participant ${id} is not assigned exactly once` };
        }
        seen.add(id);
      }
    }
    if (seen.size !== n) return { code: 'invalid_team_config', message: 'Every participant must be on a team' };
  } else if (teams && teams.length > 0) {
    return { code: 'invalid_team_config', message: `${manifest.name} does not use teams` };
  }
  return null;
}
